import React from 'react';
import { Switch } from 'features/components/Switch';
import { InfoMessage } from 'features/components/InfoMessage';
import { useSelector } from 'hooks/utils';
import { LanguageType } from 'types/index';

export enum ModulesPermissionsFlags {
	projectDesign = 'projectDesign',
	collaborators = 'collaborators'
}

const DICTIONARY = {
	[LanguageType.English]: {
		moduleViewTitle: 'Module view',
		specifyDefaultView: 'Specify Module view for specific modules in the project',
		projectDesignModule: 'Project Design module',
		collaboratorModule: 'Collaborator module',
		projectDesignDescription: 'Variables, Templates, Dependencies, Forms, Statuses',
		collaboratorDescription: 'Collaborators management',
		whatAreModules: 'What are modules?',
		modulesDescription:
			'Modules are specific functional areas within the project. Enabling a module here means it will be visible to users in the user interface. The project owner can change the setting later.',
		projectDesignDetails:
			'Access to project configuration, including variables, templates, forms, and statuses',
		collaboratorDetails:
			'Ability to manage project collaborators, including inviting, removing, and modifying permissions',
		modulesNote:
			'Modules view can be modified later for each user. Please note that this is not a permissions but default view for each user.',
		roleControlled:
			'These module settings are controlled by the selected role and cannot be modified.',
		roleControlledItem: 'Controlled by role'
	},
	[LanguageType.Norwegian]: {
		moduleViewTitle: 'Modulvisning',
		specifyDefaultView: 'Angi standardvisning for spesifikke moduler i prosjektet',
		projectDesignModule: 'Prosjektdesignmodul',
		collaboratorModule: 'Samarbeidspartnermodul',
		projectDesignDescription: 'Variabler, Maler, Avhengigheter, Skjemaer, Statuser',
		collaboratorDescription: 'Administrasjon av samarbeidspartnere',
		whatAreModules: 'Hva er moduler?',
		modulesDescription:
			'Moduler er spesifikke funksjonsområder i prosjektet. Å aktivere en modul her betyr at den blir synlig for brukere i brukergrensesnittet. Prosjekteieren kan endre innstillingen senere.',
		projectDesignDetails:
			'Tilgang til prosjektkonfigurasjon, inkludert variabler, maler, skjemaer og statuser',
		collaboratorDetails:
			'Mulighet til å administrere prosjektsamarbeidspartnere, inkludert invitasjon, fjerning og endring av tillatelser',
		modulesNote:
			'Modulvisning kan endres senere for hver bruker. Merk at dette ikke er en tillatelse, men standardvisning for hver bruker.',
		roleControlled: 'Disse modulinnstillingene styres av den valgte rollen og kan ikke endres.',
		roleControlledItem: 'Styrt av rolle'
	}
};

export type ModulesPermissions = {
	[key in ModulesPermissionsFlags]: boolean;
};

interface ModulesPermissionsSectionProps {
	modulesPermissions: ModulesPermissions;
	onUpdateModules: (modules: ModulesPermissions) => void;
	disabled?: boolean;
	isRoleControlled?: boolean;
}

export const ModulesPermissionsSection: React.FC<ModulesPermissionsSectionProps> = ({
	modulesPermissions,
	onUpdateModules,
	disabled = false,
	isRoleControlled = false
}) => {
	const language = useSelector(state => state.ui.i18n.language);
	const dictionary = DICTIONARY[language];

	const handleToggleModule = (flag: ModulesPermissionsFlags) => {
		if (disabled || isRoleControlled) return;

		const updatedPermissions = {
			...modulesPermissions,
			[flag]: !modulesPermissions[flag]
		};

		onUpdateModules(updatedPermissions);
	};

	const modules = [
		{
			flag: ModulesPermissionsFlags.projectDesign,
			label: dictionary.projectDesignModule,
			description: dictionary.projectDesignDescription,
			icon: '⚙️'
		},
		{
			flag: ModulesPermissionsFlags.collaborators,
			label: dictionary.collaboratorModule,
			description: dictionary.collaboratorDescription,
			icon: '👥'
		}
	];

	return (
		<div className="space-y-6">
			{isRoleControlled && (
				<div className="mb-6">
					<InfoMessage message={dictionary.roleControlled} />
				</div>
			)}

			<div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
				<div className="p-4 bg-gradient-to-r from-indigo-50 to-blue-50 border-b border-gray-200">
					<h3 className="text-base font-semibold text-gray-800">
						{dictionary.moduleViewTitle}
					</h3>
					<p className="text-sm text-gray-600 mt-1">{dictionary.specifyDefaultView}</p>
				</div>

				<div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
					{modules.map(({ flag, label, description, icon }) => (
						<div
							key={flag}
							className={`relative flex flex-col p-5 rounded-lg border-2 ${
								modulesPermissions[flag]
									? 'border-indigo-300 bg-indigo-50'
									: 'border-gray-200 bg-white'
							} ${
								isRoleControlled
									? 'cursor-not-allowed opacity-75'
									: 'hover:border-indigo-300 cursor-pointer'
							} transition-colors duration-200`}
							onClick={() =>
								!disabled && !isRoleControlled && handleToggleModule(flag)
							}
						>
							<div className="absolute top-4 right-4">
								<Switch
									on={modulesPermissions[flag]}
									onChange={() => handleToggleModule(flag)}
									disabled={disabled || isRoleControlled}
								/>
							</div>
							<div className="mb-3 text-3xl">{icon}</div>
							<h4 className="text-lg font-medium text-gray-800 mb-2">
								{label}
								{isRoleControlled && (
									<span className="ml-2 text-xs text-gray-500 italic">
										({dictionary.roleControlledItem})
									</span>
								)}
							</h4>
							<p className="text-sm text-gray-600">{description}</p>
						</div>
					))}
				</div>
			</div>

			<div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
				<h3 className="text-lg font-semibold text-gray-800 mb-3">
					{dictionary.whatAreModules}
				</h3>
				<p className="text-sm text-gray-600 mb-4">{dictionary.modulesDescription}</p>
				<ul className="list-disc list-inside text-sm text-gray-600 space-y-2 ml-4">
					<li>
						<span className="font-medium">{dictionary.projectDesignModule}</span> -{' '}
						{dictionary.projectDesignDetails}
					</li>
					<li>
						<span className="font-medium">{dictionary.collaboratorModule}</span> -{' '}
						{dictionary.collaboratorDetails}
					</li>
				</ul>
			</div>

			<InfoMessage message={dictionary.modulesNote} />
		</div>
	);
};
